import {
  type StudyRunCaseDashboardGateCardDto,
  StudyRunCaseDashboardGateStatus,
  type StudyScoringOutputSelectDto,
  type StudySelectDto,
  type StudySignoffSelectDto,
  StudyStakeholderRole,
  type StudyStakeholderSelectDto,
  type StudyValidationResultDto,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Tile } from '@components/shared/tile.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useAttemptMeiaStudySignoff,
  useGetMeiaAcrRegister,
  useGetMeiaStudySignoffs,
  useGetMeiaStudyStakeholders,
  useRevokeMeiaStudySignoff,
} from '@queries/meia.query';
import { CircleAlertIcon, SignatureIcon } from 'lucide-react';
import type { FC } from 'react';
import { useState } from 'react';
import { getAcrG4BlockingEntries } from './meia-acr.util';
import { formatRunCaseDate } from './meia-p4-run-cases.util';
import { formatBriefEnumLabel } from './meia-p5-brief.util';

type BriefSignoffTileProps = {
  authorName?: string | null;
  gates: StudyRunCaseDashboardGateCardDto[];
  isLoading: boolean;
  onAddDecisionMaker?: () => void;
  onOpenAcrRegister?: (focusEntryId?: string | null) => void;
  scoringOutput?: StudyScoringOutputSelectDto | null;
  study: StudySelectDto;
  studyId: string;
  validationResults: StudyValidationResultDto[];
};

type PendingSignoffAction = 'approve' | 'unapprove' | null;

export const BriefSignoffTile: FC<BriefSignoffTileProps> = (props) => {
  const {
    authorName,
    gates,
    isLoading,
    onAddDecisionMaker,
    onOpenAcrRegister,
    scoringOutput,
    study,
    studyId,
    validationResults,
  } = props;
  const [pendingAction, setPendingAction] = useState<PendingSignoffAction>(null);
  const { data: signoffs = [] } = useGetMeiaStudySignoffs({ studyId });
  const { data: stakeholders = [] } = useGetMeiaStudyStakeholders({ studyId });
  const { isPending: isApproving, mutateAsync: approveStudy } = useAttemptMeiaStudySignoff();
  const { isPending: isUnapproving, mutateAsync: unapproveStudy } = useRevokeMeiaStudySignoff();
  const { data: acrEntries = [] } = useGetMeiaAcrRegister({ studyId });
  const acrBlockingEntries = getAcrG4BlockingEntries(acrEntries);
  const blockingValidationCount = validationResults.filter((result) => result.blocking).length;
  const failedGateCount = gates.filter((gate) => gate.status === StudyRunCaseDashboardGateStatus.FAIL).length;
  const isAcrBlocked = acrBlockingEntries.length > 0;
  const isBlocked = blockingValidationCount > 0 || failedGateCount > 0 || isAcrBlocked;
  const decisionMaker =
    stakeholders.find((stakeholder) => stakeholder.role === StudyStakeholderRole.DECISION_MAKER) ?? null;
  const isMissingDecisionMaker = !decisionMaker;
  const activeG4Signoff = signoffs.find((signoff) => signoff.gate === 'G4' && !signoff.revokedAt) ?? null;
  const canApprove = study.canSignOffStudy && !activeG4Signoff && !isMissingDecisionMaker;
  const canUnapprove = study.canSignOffStudy && Boolean(activeG4Signoff);
  const isMutating = isApproving || isUnapproving;

  const handleConfirmAction = async (): Promise<void> => {
    if (!pendingAction) return;

    try {
      if (pendingAction === 'approve') {
        await approveStudy({
          gate: 'G4',
          rationale: 'Decision maker approved G4 brief.',
          signature: null,
          studyId,
        });
        successToast({ description: 'Study approved.' });
      } else {
        await unapproveStudy({ gate: 'G4', studyId });
        successToast({ description: 'Study approval revoked.' });
      }
      setPendingAction(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update study approval.';
      errorToast({ description: message });
    }
  };

  if (isLoading) {
    return (
      <Tile header={{ title: 'DM Sign-off · G4' }}>
        <Skeleton className="h-28 w-full" />
      </Tile>
    );
  }

  return (
    <Tile
      contentClassName="gap-3"
      header={{
        title: (
          <span className="inline-flex items-center gap-2">
            <SignatureIcon className="size-4 text-primary" />
            DM Sign-off · G4
          </span>
        ),
      }}
    >
      {isBlocked ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <div className="font-medium">G4 sign-off blocked</div>
          {(failedGateCount > 0 || blockingValidationCount > 0) && (
            <p className="mt-1">
              {failedGateCount} failed gate{failedGateCount === 1 ? '' : 's'} and {blockingValidationCount} blocking
              validation finding{blockingValidationCount === 1 ? '' : 's'} must be resolved before decision approval.
            </p>
          )}
          {isAcrBlocked && (
            <div className="mt-1">
              <p>
                {acrBlockingEntries.length === 1
                  ? 'An open Risk has no mitigation and must be resolved before sign-off:'
                  : `${acrBlockingEntries.length} open Risks have no mitigation and must be resolved before sign-off:`}
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {acrBlockingEntries.map((entry) => (
                  <li key={entry.id}>
                    <span className="font-mono">{entry.acrCode}</span> — {entry.title || entry.statement}
                  </li>
                ))}
              </ul>
              {onOpenAcrRegister && (
                <Button
                  className="mt-1 h-fit px-0 py-0"
                  onClick={() => onOpenAcrRegister(acrBlockingEntries[0]?.id ?? null)}
                  size="sm"
                  type="button"
                  variant="text"
                >
                  Open ACR Register
                </Button>
              )}
            </div>
          )}
        </div>
      ) : activeG4Signoff ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          <div className="font-medium">Study approved</div>
          <p className="mt-1">
            Approved by {formatSignoffUserName(activeG4Signoff)} at {formatRunCaseDate(activeG4Signoff.timestamp)}.
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          <div className="font-medium">No blocking readiness findings detected</div>
          <p className="mt-1">
            {isMissingDecisionMaker ? 'Add a Decision Maker before G4 approval.' : 'Ready for Decision Maker approval.'}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
        {gates.map((gate) => {
          const isDecisionMakerGate = gate.gate === 'G4';
          const showDecisionMakerBlocked = isDecisionMakerGate && isMissingDecisionMaker;

          return (
            <div
              className="rounded-md border border-gray-200 px-3 py-2 text-xs"
              key={gate.gate}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900">{gate.label}</span>
                <Badge
                  size="sm"
                  variant={showDecisionMakerBlocked ? 'destructive' : gateBadgeVariant(gate.status)}
                >
                  {showDecisionMakerBlocked ? 'Blocked' : formatBriefEnumLabel(gate.status)}
                </Badge>
              </div>
              <p className="mt-1 text-gray-500">
                {showDecisionMakerBlocked ? 'A Decision Maker is required before G4 sign-off.' : gate.detail}
              </p>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-md border border-gray-200 px-3 py-2 text-xs">
          <div className="font-medium text-gray-900">{authorName || 'Owner unavailable'}</div>
          <BlueLabel
            className="text-xs"
            text="Author"
          />
          <div className="mt-1 text-gray-500">
            {scoringOutput ? `Asserted at ${formatRunCaseDate(scoringOutput.scoredAt)}` : 'Awaiting run output'}
          </div>
        </div>
        <div className="rounded-md border border-gray-200 px-3 py-2 text-xs">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-gray-900">
                {activeG4Signoff ? (
                  formatSignoffUserName(activeG4Signoff)
                ) : decisionMaker ? (
                  formatStakeholderUserName(decisionMaker)
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-red-700">
                    <CircleAlertIcon className="size-4" />
                    No Decision Maker
                  </span>
                )}
              </div>
              {!isMissingDecisionMaker && (
                <BlueLabel
                  className="text-xs"
                  text="Decision Maker"
                />
              )}
              {!isMissingDecisionMaker && (
                <div className="mt-1 text-gray-500">
                  {activeG4Signoff
                    ? `Approved at ${formatRunCaseDate(activeG4Signoff.timestamp)}`
                    : isBlocked
                      ? 'Blocked by readiness findings'
                      : 'Awaiting sign-off'}
                </div>
              )}
            </div>
            {(canApprove || canUnapprove) && (
              <Button
                disabled={isBlocked && canApprove}
                onClick={() => setPendingAction(canApprove ? 'approve' : 'unapprove')}
                size="sm"
                type="button"
                variant={canUnapprove ? 'outline' : 'primary'}
              >
                {canUnapprove ? 'Unapprove' : 'Approve Study'}
              </Button>
            )}
          </div>
          {isMissingDecisionMaker && study.canManageStakeholders && onAddDecisionMaker && (
            <Button
              className="mt-1 h-fit px-0 py-0"
              onClick={onAddDecisionMaker}
              size="sm"
              type="button"
              variant="text"
            >
              + Add Decision Maker
            </Button>
          )}
        </div>
      </div>
      <Modal
        centered
        contentClassname="text-sm text-gray-600"
        footer={{
          leftButton: {
            children: 'Cancel',
            disabled: isMutating,
            onClick: () => setPendingAction(null),
            type: 'button',
          },
          rightButton: {
            children: pendingAction === 'approve' ? 'Approve Study' : 'Unapprove Study',
            disabled: isMutating,
            onClick: () => void handleConfirmAction(),
            type: 'button',
            variant: pendingAction === 'approve' ? 'primary' : 'destructive',
          },
        }}
        header={{
          title: pendingAction === 'approve' ? 'Approve Study?' : 'Unapprove Study?',
        }}
        isOpen={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        width="520px"
      >
        {pendingAction === 'approve'
          ? 'This records your Decision Maker approval for G4.'
          : 'This revokes the active Decision Maker approval while preserving the audit history.'}
      </Modal>
    </Tile>
  );
};

const formatSignoffUserName = (signoff: StudySignoffSelectDto): string => {
  const profile = signoff.user?.profile;
  const fullName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
  return fullName || signoff.userId;
};

const formatStakeholderUserName = (stakeholder: StudyStakeholderSelectDto): string => {
  const profile = stakeholder.user?.profile;
  const fullName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
  return fullName || stakeholder.user?.email || stakeholder.userId;
};

const gateBadgeVariant = (status: StudyRunCaseDashboardGateStatus): BadgeProps['variant'] => {
  switch (status) {
    case StudyRunCaseDashboardGateStatus.FAIL:
      return 'destructive';
    case StudyRunCaseDashboardGateStatus.PASS:
      return 'success';
    case StudyRunCaseDashboardGateStatus.PENDING:
      return 'pending';
    case StudyRunCaseDashboardGateStatus.SOFT:
      return 'warning';
  }
};
